/**
 * <s-collection-page> — progressive enhancements for templates/collection.liquid.
 *
 * Works alongside <s-live-form> (which owns filtering/sorting and partial swaps):
 *   [data-description] + [data-description-toggle] — "Read more" clamp toggle, shown only when the intro overflows.
 *   [data-filter-panel]       — stable wrapper of the filter partial. Hosts the price slider handlers,
 *                               remembers open/closed filter groups across partial swaps, and closes
 *                               the mobile drawer when the viewport reaches the desktop layout.
 *   [data-price-range]        — unnamed range thumbs synced to the named number inputs; only the
 *                               number inputs are submitted, so an untouched slider adds no filter.
 *   [data-sort-mobile] radios — mirror into the form's select[name="sort_by"], which submits.
 *   [data-grid-root]          — receives data-columns (3|4) and data-columns-mobile (1|2) from [data-columns-toggle] buttons, persisted per scope.
 *   [data-load-more]          — real next-page link; enhanced to append the next page's products in place.
 */
const DESKTOP = window.matchMedia('(min-width: 990px)');
// Products-per-row choice, remembered per layout so it carries over between collections.
const COLUMN_SCOPES = {
  desktop: { key: 'maddelin:collection-columns', attribute: 'data-columns', allowed: ['3', '4'], fallback: '4' },
  mobile: { key: 'maddelin:collection-columns-mobile', attribute: 'data-columns-mobile', allowed: ['1', '2'], fallback: '2' },
};
const readStored = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeStored = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable: keep the in-page choice */ }
};

class SCollectionPage extends HTMLElement {
  #ac;
  #groupState = new Map();
  #loading = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('s-live-form:updated', this.#onUpdated, { signal });
    this.addEventListener('toggle', this.#onToggle, { signal, capture: true });

    const panel = this.querySelector('[data-filter-panel]');
    if (panel) {
      // Stop slider drags from reaching <s-live-form>; the synced number input submits instead.
      panel.addEventListener('input', this.#onPriceInput, { signal });
      panel.addEventListener('change', this.#onPriceCommit, { signal });
    }

    DESKTOP.addEventListener('change', this.#onViewportChange, { signal });

    this.#initDescription();
    this.#applyColumns();
    this.#initPriceRanges();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- description ---------- */
  #initDescription() {
    const text = this.querySelector('[data-description]');
    const toggle = this.querySelector('[data-description-toggle]');
    if (!text || !toggle) return;
    if (text.scrollHeight > text.clientHeight + 2) toggle.hidden = false;
  }

  /* ---------- clicks ---------- */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const descToggle = target.closest('[data-description-toggle]');
    if (descToggle) {
      const text = this.querySelector('[data-description]');
      const expanded = descToggle.getAttribute('aria-expanded') === 'true';
      descToggle.setAttribute('aria-expanded', String(!expanded));
      text?.classList.toggle('line-clamp-2', expanded);
      descToggle.textContent = expanded ? descToggle.dataset.labelMore : descToggle.dataset.labelLess;
      return;
    }

    const columnsButton = target.closest('[data-columns-toggle]');
    if (columnsButton) {
      const config = COLUMN_SCOPES[columnsButton.dataset.columnsScope];
      if (config) writeStored(config.key, columnsButton.dataset.columnsToggle);
      this.#applyColumns();
      return;
    }

    const loadMore = target.closest('[data-load-more]');
    if (loadMore instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#loadMore(loadMore);
    }
  };

  #applyColumns() {
    const root = this.querySelector('[data-grid-root]');
    for (const [scope, config] of Object.entries(COLUMN_SCOPES)) {
      const stored = readStored(config.key);
      const value = config.allowed.includes(stored) ? stored : config.fallback;
      root?.setAttribute(config.attribute, value);
      for (const button of this.querySelectorAll(`[data-columns-toggle][data-columns-scope="${scope}"]`)) {
        button.setAttribute('aria-pressed', String(button.dataset.columnsToggle === value));
      }
    }
  }

  /* ---------- mobile sort ---------- */
  #onChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.matches('[data-sort-mobile]')) return;
    const select = this.querySelector('select[name="sort_by"]');
    if (!select) return;
    select.value = radio.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    radio.closest('[popover]')?.hidePopover?.();
  };

  /* ---------- price slider ---------- */
  #initPriceRanges() {
    for (const range of this.querySelectorAll('[data-price-range]')) this.#paintRange(range);
  }

  #paintRange(range) {
    const max = Number(range.dataset.rangeMax) || 1;
    const minThumb = range.querySelector('[data-price-thumb="min"]');
    const maxThumb = range.querySelector('[data-price-thumb="max"]');
    const fill = range.querySelector('[data-price-fill]');
    if (!minThumb || !maxThumb || !fill) return;
    const start = (Number(minThumb.value) / max) * 100;
    const end = (Number(maxThumb.value) / max) * 100;
    fill.style.insetInlineStart = `${start}%`;
    fill.style.width = `${Math.max(0, end - start)}%`;
  }

  #onPriceInput = (event) => {
    const thumb = event.target;
    if (!(thumb instanceof HTMLInputElement)) return;
    const range = thumb.closest('[data-price-range]');
    if (!range) return;

    if (thumb.matches('[data-price-thumb]')) {
      event.stopPropagation();
      const minThumb = range.querySelector('[data-price-thumb="min"]');
      const maxThumb = range.querySelector('[data-price-thumb="max"]');
      if (Number(minThumb.value) > Number(maxThumb.value)) {
        if (thumb === minThumb) minThumb.value = maxThumb.value;
        else maxThumb.value = minThumb.value;
      }
      this.#paintRange(range);
      return;
    }

    if (thumb.matches('[data-price-input]')) {
      // Typing in the number fields moves the thumbs; <s-live-form> handles the debounced submit.
      const which = thumb.dataset.priceInput;
      const target = range.querySelector(`[data-price-thumb="${which}"]`);
      const max = Number(range.dataset.rangeMax) || 0;
      if (target) target.value = thumb.value === '' ? (which === 'min' ? '0' : String(max)) : thumb.value;
      this.#paintRange(range);
    }
  };

  #onPriceCommit = (event) => {
    const thumb = event.target;
    if (!(thumb instanceof HTMLInputElement) || !thumb.matches('[data-price-thumb]')) return;
    event.stopPropagation();
    const range = thumb.closest('[data-price-range]');
    const max = Number(range.dataset.rangeMax) || 0;
    const which = thumb.dataset.priceThumb;
    const input = range.querySelector(`[data-price-input="${which}"]`);
    if (!input) return;
    const atBound = which === 'min' ? Number(thumb.value) <= 0 : Number(thumb.value) >= max;
    input.value = atBound ? '' : thumb.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /* ---------- filter group state across partial swaps ---------- */
  #onToggle = (event) => {
    const group = event.target;
    if (group instanceof HTMLDetailsElement && group.dataset.filterGroup) {
      this.#groupState.set(group.dataset.filterGroup, group.open);
    }
  };

  #onUpdated = () => {
    for (const group of this.querySelectorAll('details[data-filter-group]')) {
      const saved = this.#groupState.get(group.dataset.filterGroup);
      if (saved !== undefined) group.open = saved;
    }
    this.#initPriceRanges();
    this.#applyColumns();
    for (const radio of this.querySelectorAll('[data-sort-mobile]')) {
      const select = this.querySelector('select[name="sort_by"]');
      if (select) radio.checked = radio.value === select.value;
    }
  };

  #onViewportChange = (event) => {
    if (!event.matches) return;
    for (const popover of this.querySelectorAll('[popover]')) {
      if (popover.matches(':popover-open')) popover.hidePopover();
    }
  };

  /* ---------- load more ---------- */
  async #loadMore(link) {
    if (this.#loading) return;
    this.#loading = true;
    link.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(link.href, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = this.querySelector('#product-grid');
      const incoming = doc.querySelectorAll('#product-grid > li');
      const firstNew = incoming[0];
      grid?.append(...incoming);

      const footer = this.querySelector('[data-grid-footer]');
      const nextFooter = doc.querySelector('[data-grid-footer]');
      if (footer && nextFooter) footer.replaceWith(nextFooter);

      firstNew?.querySelector('a[href]')?.focus({ preventScroll: true });
    } catch {
      window.location.href = link.href;
    } finally {
      this.#loading = false;
      link.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-collection-page')) {
  customElements.define('s-collection-page', SCollectionPage);
}
